import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

/**
 * Hook to set up real-time synchronization for tasks and events
 * Listens to Supabase Realtime changes and invalidates relevant caches
 */
export function useRealtimeSync() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    // Create channels for real-time updates
    const tasksChannel = supabase
      .channel('family_tasks_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'family_tasks'
        },
        (payload) => {
          console.log('🔄 Task change detected:', payload);
          
          // Invalidate tasks cache for the affected family
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          
          if (newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['tasks', newRecord.family_id] });
            queryClient.invalidateQueries({ queryKey: ['task-lists', newRecord.family_id] });
            queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', newRecord.family_id] });
          }
          
          // Also invalidate if we have the old record
          if (oldRecord?.family_id && oldRecord.family_id !== newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['tasks', oldRecord.family_id] });
            queryClient.invalidateQueries({ queryKey: ['task-lists', oldRecord.family_id] });
            queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', oldRecord.family_id] });
          }
        }
      )
      .subscribe();

    const eventsChannel = supabase
      .channel('family_events_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'family_events'
        },
        (payload) => {
          console.log('🔄 Event change detected:', payload);
          
          // Invalidate events cache for the affected family
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          
          if (newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['events', newRecord.family_id] });
          }
          
          if (oldRecord?.family_id && oldRecord.family_id !== newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['events', oldRecord.family_id] });
          }
        }
      )
      .subscribe();

    const eventAttendeesChannel = supabase
      .channel('event_attendees_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'family_event_attendees'
        },
        () => {
          console.log('🔄 Event attendees change detected');
          
          // Invalidate all events queries since we don't know which family is affected
          queryClient.invalidateQueries({ queryKey: ['events'] });
        }
      )
      .subscribe();

    const taskListsChannel = supabase
      .channel('task_lists_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_lists'
        },
        (payload) => {
          console.log('🔄 Task list change detected:', payload);
          
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          
          if (newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['task-lists', newRecord.family_id] });
            queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', newRecord.family_id] });
            queryClient.invalidateQueries({ queryKey: ['tasks', newRecord.family_id] });
          }
          
          if (oldRecord?.family_id && oldRecord.family_id !== newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['task-lists', oldRecord.family_id] });
            queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', oldRecord.family_id] });
            queryClient.invalidateQueries({ queryKey: ['tasks', oldRecord.family_id] });
          }
        }
      )
      .subscribe();

    const personProfilesChannel = supabase
      .channel('person_profiles_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'person_profiles'
        },
        (payload) => {
          console.log('🔄 Person profile change detected:', payload);
          
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          
          if (newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['person-profiles', newRecord.family_id] });
          }
          
          if (oldRecord?.family_id && oldRecord.family_id !== newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['person-profiles', oldRecord.family_id] });
          }
        }
      )
      .subscribe();

    const documentsChannel = supabase
      .channel('family_documents_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'family_documents'
        },
        (payload) => {
          console.log('🔄 Document change detected:', payload);
          
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          
          if (newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['family-documents', newRecord.family_id] });
          }
          
          if (oldRecord?.family_id && oldRecord.family_id !== newRecord?.family_id) {
            queryClient.invalidateQueries({ queryKey: ['family-documents', oldRecord.family_id] });
          }
        }
      )
      .subscribe();

    // Cleanup function to unsubscribe from channels
    return () => {
      supabase.removeChannel(tasksChannel);
      supabase.removeChannel(eventsChannel);
      supabase.removeChannel(eventAttendeesChannel);
      supabase.removeChannel(taskListsChannel);
      supabase.removeChannel(personProfilesChannel);
      supabase.removeChannel(documentsChannel);
    };
  }, [user, queryClient]);
}